import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom"; // Renamed Link to avoid conflict with MUI Link
import { excavatorService } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

// Material UI components
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Alert,
  AlertTitle,
  useTheme, // Import useTheme
} from "@mui/material";

// Material UI icons
import AddCircleIcon from "@mui/icons-material/AddCircleOutline"; // Using outlined version
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import BuildIcon from "@mui/icons-material/Build"; // Main icon for the page

// Import our page container for consistent layout
import PageContainer from "../components/PageContainer";
import ContentSection from "../components/ContentSection"; // This will be used as a wrapper

const ExcavatorList = () => {
  const { isAdmin } = useContext(AuthContext);
  const { t } = useLanguage();
  const theme = useTheme(); // Get theme object

  const [excavators, setExcavators] = useState([]);
  const [allExcavators, setAllExcavators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    pageSize: 10,
    totalItems: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [quickSearchMode, setQuickSearchMode] = useState("all");
  const [sortBy, setSortBy] = useState("manufacturer"); // Default sort by manufacturer
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTimer, setSearchTimer] = useState(null);

  const fetchAllExcavators = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all, client-side pagination/filtering will be applied
      const response = await excavatorService.getAll({ limit: 10000 }); // Fetch a large number
      const data = response.data?.data || response.data || [];
      setAllExcavators(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching all excavators:", err);
      setError(err.message || t("errorFetchingExcavators"));
      setAllExcavators([]); // Ensure it's an empty array on error
    } finally {
      setLoading(false);
    }
  }, [t]);


  useEffect(() => {
    fetchAllExcavators();
  }, [fetchAllExcavators]);

  const processedExcavators = React.useMemo(() => {
    let filtered = [...allExcavators];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = allExcavators.filter((excavator) => {
        if (quickSearchMode === "manufacturer") return (excavator.manufacturer || "").toLowerCase().includes(term);
        if (quickSearchMode === "model") return (excavator.model || "").toLowerCase().includes(term);
        if (quickSearchMode === "year") return (excavator.year || "").toString().includes(term);
        if (quickSearchMode === "serialNumber") return (excavator.serialNumber || "").toLowerCase().includes(term);
        return Object.values(excavator).some(val => String(val).toLowerCase().includes(term));
      });
    }

    filtered.sort((a, b) => {
      let valA = a[sortBy] || "";
      let valB = b[sortBy] || "";
      // Ensure consistent string comparison
      valA = String(valA).toLowerCase();
      valB = String(valB).toLowerCase();
      return sortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });

    return filtered;
  }, [allExcavators, searchTerm, quickSearchMode, sortBy, sortDirection]);

  useEffect(() => {
    const paginatedData = processedExcavators.slice(
      pagination.currentPage * pagination.pageSize,
      pagination.currentPage * pagination.pageSize + pagination.pageSize
    );
    setExcavators(paginatedData);
    setPagination(prev => ({ ...prev, totalItems: processedExcavators.length }));
  }, [processedExcavators, pagination.currentPage, pagination.pageSize]);


  useEffect(() => {
    if (searchTimer) clearTimeout(searchTimer);
    const timer = setTimeout(() => {
      if (pagination.currentPage !== 0) {
        setPagination((prev) => ({ ...prev, currentPage: 0 }));
      }
    }, 300);
    setSearchTimer(timer);
    return () => { if (searchTimer) clearTimeout(searchTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, quickSearchMode]);


  const handleSort = (field) => {
    const isAsc = sortBy === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortBy(field);
  };

  const resetSearch = () => {
    setSearchTerm("");
    setQuickSearchMode("all");
    // setPagination(prev => ({ ...prev, currentPage: 0 })); // Already handled by useEffect
  };

  const handleChangePage = (event, newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination(prev => ({ ...prev, pageSize: parseInt(event.target.value, 10), currentPage: 0 }));
  };

  if (loading && allExcavators.length === 0) {
    return (
      <PageContainer>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", flexDirection: "column", gap: 2 }}>
          <CircularProgress size={50} />
          <Typography variant="body1" color="text.secondary">{t("loadingExcavators")}</Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={t("excavatorList")}>
      <ContentSection // Using ContentSection as the main page wrapper/card
        title={t("excavatorList")}
        icon={<BuildIcon sx={{color: "primary.main"}}/>}
        elevation={0} // PageContainer might provide its own elevation or background
        sx={{mb:0}} // No margin if it's the main page content block
        actions={
          isAdmin() && (
            <Button
              component={RouterLink}
              to="/excavators/add"
              variant="contained"
              color="primary"
              startIcon={<AddCircleIcon />}
            >
              {t("addExcavator")}
            </Button>
          )
        }
      >
        {error && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }} action={
            <Button color="inherit" size="small" onClick={fetchAllExcavators} startIcon={<RefreshIcon />}>
              {t("retry")}
            </Button>
          }>
            <AlertTitle>{t("error")}</AlertTitle>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2, borderRadius: theme.shape.borderRadius }}> {/* Search bar Paper */}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7} lg={8}>
              <TextField
                fullWidth variant="outlined" size="small" label={t("search")} value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("searchExcavatorsPlaceholder") || "Search by manufacturer, model, year..."}
                InputProps={{
                  startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                  endAdornment: searchTerm && (<InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")} edge="end"><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>),
                }}
              />
            </Grid>
            <Grid item xs={12} md={5} lg={4}>
              <Stack direction="row" spacing={1} justifyContent={{xs: 'flex-start', md: 'flex-end'}}>
                <FormControl size="small" sx={{ minWidth: 150, flexGrow: 1 }}>
                  <InputLabel id="quick-search-mode-label">{t("searchBy")}</InputLabel>
                  <Select labelId="quick-search-mode-label" value={quickSearchMode} onChange={(e) => setQuickSearchMode(e.target.value)} label={t("searchBy")}>
                    <MenuItem value="all">{t("allFields")}</MenuItem>
                    <MenuItem value="manufacturer">{t("manufacturer")}</MenuItem>
                    <MenuItem value="model">{t("model")}</MenuItem>
                    <MenuItem value="year">{t("year")}</MenuItem>
                    <MenuItem value="serialNumber">{t("serialNumber")}</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="text" size="small" onClick={resetSearch} startIcon={<ClearIcon />}>
                  {t("reset")}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} sx={{ borderRadius: theme.shape.borderRadius }}>
          <Table stickyHeader>
            <TableHead> {/* TableHead styling is now primarily from theme.js MuiTableCell override */}
              <TableRow>
                {['manufacturer', 'model', 'serialNumber', 'year'].map(headCell => (
                  <TableCell key={headCell} sortDirection={sortBy === headCell ? sortDirection : false}>
                    <TableSortLabel active={sortBy === headCell} direction={sortBy === headCell ? sortDirection : 'asc'} onClick={() => handleSort(headCell)}>
                      {t(headCell)}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="right">{t("actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && excavators.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{mt:1}}>{t("loading")}</Typography>
                  </TableCell>
                </TableRow>
              ) : excavators.length > 0 ? (
                excavators.map((excavator) => (
                  <TableRow hover key={excavator._id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell><Typography variant="body2" fontWeight="medium">{excavator.manufacturer}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{excavator.model}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{excavator.serialNumber}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{excavator.year}</Typography></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t("view")}>
                          <IconButton component={RouterLink} to={`/excavators/${excavator._id}`} size="small" color="info">
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {isAdmin() && (
                          <Tooltip title={t("edit")}>
                            <IconButton component={RouterLink} to={`/excavators/edit/${excavator._id}`} size="small" color="warning">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t("reports")}>
                          <IconButton component={RouterLink} to={`/reports?search=${excavator.serialNumber}`} size="small" color="secondary">
                            <DescriptionIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <Typography color="text.secondary" gutterBottom>
                        {searchTerm ? t("noMatchingResults") : t("noExcavatorsFound")}
                      </Typography>
                      {isAdmin() && !searchTerm && (
                        <Button variant="contained" component={RouterLink} to="/excavators/add" startIcon={<AddCircleIcon />} sx={{ mt: 2 }}>
                          {t("addFirstExcavator")}
                        </Button>
                      )}
                      {searchTerm && (
                        <Button variant="outlined" onClick={resetSearch} startIcon={<ClearIcon />} sx={{ mt: 2 }}>
                          {t("clearSearch")}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={pagination.totalItems}
            rowsPerPage={pagination.pageSize}
            page={pagination.currentPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t("of")} ${count !== -1 ? count : `${t("moreThan")} ${to}`}`}
          />
        </TableContainer>
      </ContentSection>
    </PageContainer>
  );
};

export default ExcavatorList;
